import { createContext, useContext, useEffect, useState, ReactNode, useCallback } from "react";
import { User, Session } from "@supabase/supabase-js";
import { supabase } from "@/integrations/supabase/client";

interface AuthContextType {
  user: User | null;
  session: Session | null;
  loading: boolean;
  userRole: string | null | undefined; // undefined = loading, null = no role
  emailVerified: boolean | null; // null = loading
  isAdmin: boolean;
  signUp: (email: string, password: string, fullName: string) => Promise<{ error: Error | null }>;
  signIn: (email: string, password: string) => Promise<{ error: Error | null }>;
  signOut: () => Promise<void>;
  refreshProfile: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const [userRole, setUserRole] = useState<string | null | undefined>(undefined);
  const [emailVerified, setEmailVerified] = useState<boolean | null>(null);
  const [isAdmin, setIsAdmin] = useState(false);

  const fetchUserRole = useCallback(async (userId: string) => {
    const { data } = await supabase.rpc("get_user_role", { _user_id: userId });
    setUserRole((data as string) || null);
  }, []);

  const fetchAdminStatus = useCallback(async (userId: string) => {
    const { data } = await supabase.rpc("is_admin", { _user_id: userId });
    setIsAdmin(!!data);
  }, []);

  const fetchProfile = useCallback(async (currentUser: User) => {
    const { data, error } = await supabase
      .from("profiles")
      .select("email_verified")
      .eq("user_id", currentUser.id)
      .maybeSingle();

    if (!data && error?.code !== "PGRST116") {
      const isGoogleUser = currentUser.app_metadata?.provider === "google" ||
        currentUser.app_metadata?.providers?.includes("google");

      await supabase.from("profiles").insert({
        user_id: currentUser.id,
        email: currentUser.email,
        full_name: currentUser.user_metadata?.full_name || "",
        email_verified: isGoogleUser,
      });
      setEmailVerified(isGoogleUser);
      return;
    }

    if (data) {
      const isGoogleUser = currentUser.app_metadata?.provider === "google" ||
        currentUser.app_metadata?.providers?.includes("google");
      // Self-heal: if the auth account itself is already confirmed, the profile
      // flag must never lock the user out of the app.
      const authConfirmed = Boolean(
        (currentUser as unknown as { email_confirmed_at?: string }).email_confirmed_at ||
        (currentUser as unknown as { confirmed_at?: string }).confirmed_at,
      );

      if (!data.email_verified && (isGoogleUser || authConfirmed)) {
        await supabase
          .from("profiles")
          .update({ email_verified: true })
          .eq("user_id", currentUser.id);
        setEmailVerified(true);
      } else {
        setEmailVerified(data.email_verified ?? false);
      }
    } else {
      setEmailVerified(false);
    }

  }, []);

  const refreshProfile = useCallback(async () => {
    if (user) {
      await Promise.all([fetchProfile(user), fetchUserRole(user.id), fetchAdminStatus(user.id)]);
    }
  }, [user, fetchProfile, fetchUserRole, fetchAdminStatus]);

  useEffect(() => {
    let currentUserId: string | null = null;

    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (_event, session) => {
        setSession(session);
        // Keep the same user object reference when it's the same user, so that
        // token refreshes (which fire when the tab regains focus) don't retrigger
        // effects depending on `user` across the app.
        setUser((prev) => (prev && prev.id === session?.user?.id ? prev : session?.user ?? null));
        setLoading(false);

        const nextId = session?.user?.id ?? null;
        if (session?.user && nextId !== currentUserId) {
          currentUserId = nextId;
          setTimeout(() => {
            fetchUserRole(session.user.id);
            fetchProfile(session.user);
            fetchAdminStatus(session.user.id);
          }, 0);
        } else if (!session?.user) {
          currentUserId = null;
          setUserRole(undefined);
          setEmailVerified(null);
          setIsAdmin(false);
        }
      }
    );

    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      setUser((prev) => (prev && prev.id === session?.user?.id ? prev : session?.user ?? null));
      setLoading(false);

      if (session?.user && session.user.id !== currentUserId) {
        currentUserId = session.user.id;
        fetchUserRole(session.user.id);
        fetchProfile(session.user);
        fetchAdminStatus(session.user.id);
      }
    });

    return () => subscription.unsubscribe();
  }, [fetchUserRole, fetchProfile, fetchAdminStatus]);

  const signUp = async (email: string, password: string, fullName: string) => {
    const { error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        data: { full_name: fullName },
      },
    });
    return { error: error ? new Error(error.message) : null };
  };

  const signIn = async (email: string, password: string) => {
    const { error } = await supabase.auth.signInWithPassword({ email, password });
    return { error: error ? new Error(error.message) : null };
  };

  const signOut = async () => {
    await supabase.auth.signOut();
    setUserRole(undefined);
    setEmailVerified(null);
    setIsAdmin(false);
  };

  return (
    <AuthContext.Provider
      value={{ user, session, loading, userRole, emailVerified, isAdmin, signUp, signIn, signOut, refreshProfile }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}
